import TextBlockStyles from './TextBlockStyles';
import { textBlockCss } from '@/constants/textBlockStyles';
import { useState, useRef, lazy, Suspense } from 'react';
import { flushSync } from 'react-dom';
import Modal from '@/components/ui/Modal';
import RichMessagePreview from './RichMessagePreview';
const EmojiPicker = lazy(() => import('./RichMessageEmojiPicker'));
import type { RichTextItem, RichTextStyle } from '@/types/ads';
import { useTranslation } from '@/hooks/useTranslation';
import Button from '@/components/ui/Button';
import RichMessageImage from './RichMessageImage';
import StickerLibraryPicker from './StickerLibraryPicker';
import { GripVertical, X, ChevronDown } from 'lucide-react';
import { messageFallback } from '@/constants/richMessage';

export default function RichMessageEditor({ items, onChange, error, onPreset, onUploadingChange, defaultStyle = {}, backgroundColor = '#111827' }: {
  defaultStyle?: RichTextStyle; backgroundColor?: string;
  items: RichTextItem[]; onChange: (items: RichTextItem[]) => void; error?: string;
  onUploadingChange: (uploading: boolean) => void;
  onPreset: (preset: 'simple' | 'promotion' | 'celebration') => void;
}) {
  const { t } = useTranslation();
  const l = t.richMessage;
  const imageInput = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [picker, setPicker] = useState<'EMOJI' | 'STICKER' | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const update = (index: number, patch: Partial<RichTextItem>) => onChange(items.map((item, i) => i === index ? { ...item, ...patch } : item));
  const add = (item: RichTextItem) => {
    if (items.length < 24) {
      // Images and emojis have no font: inherit from the nearest preceding text.
      const previous = [...items].reverse().find(existing => existing.kind === 'TEXT');
      const style = {
        fontFamily: 'roboto_regular', fontSize: 24, color: '#ffffff',
        bold: false, italic: false, underline: false,
        ...defaultStyle,
        ...Object.fromEntries(Object.entries(previous?.style ?? {}).filter(([, value]) => value !== undefined)),
      };
      onChange([...items, item.kind === 'TEXT' ? { ...item, style } : item]);
    }
    setPicker(null);
  };
  const choose = (item: RichTextItem) => {
    if (editingIndex === null) add(item);
    else onChange(items.map((existing, index) => index === editingIndex ? item : existing));
    setPicker(null);
    setEditingIndex(null);
  };
  const move = (from: number, to: number) => {
    if (uploading || to < 0 || to >= items.length || from === to) return;
    const next = [...items]; const [item] = next.splice(from, 1); next.splice(to, 0, item); onChange(next);
  };
  return <fieldset disabled={uploading} className="space-y-3">
    <div><h3 className="text-sm font-medium">{l.title}</h3><p className="text-xs text-gray-500">{l.help}</p></div>
    {!messageFallback(items).trim() && <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-gray-500">{l.templates}</span>
      {(['restaurant', 'shop', 'announcement'] as const).map(template => <Button key={template} type="button" variant="outline" size="xs" onClick={() => {
        onPreset(template === 'announcement' ? 'celebration' : 'promotion');
        onChange([
          template === 'restaurant' ? { kind: 'EMOJI', text: '🍕' } : { kind: 'STICKER', sticker: template === 'shop' ? 'gift' : 'party' },
          { kind: 'TEXT', text: l[template === 'restaurant' ? 'restaurantMessage' : template === 'shop' ? 'shopMessage' : 'announcementMessage'] },
        ]);
      }}>{l[template]}</Button>)}
    </div>}
    <div className="flex flex-wrap items-center gap-2">
      {items.map((item, index) => <div key={index} data-message-block={index}
        onDragOver={event => event.preventDefault()}
        onDrop={event => { event.preventDefault(); if (dragIndex !== null) move(dragIndex, index); setDragIndex(null); }}
        className={`flex min-h-14 max-w-full items-center gap-1 rounded border bg-white px-2 ${dragIndex === index ? 'border-indigo-500 opacity-60' : 'border-gray-200'} ${item.kind === 'TEXT' ? 'py-2' : 'h-14'}`}>
        <button type="button" draggable={!uploading} aria-label={`${l.reorder} ${index + 1}`}
          onDragStart={event => { event.dataTransfer.setData('text/plain', String(index)); setDragIndex(index); }}
          onDragEnd={() => setDragIndex(null)}
          onKeyDown={event => {
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
              event.preventDefault(); move(index, index + (event.key === 'ArrowLeft' ? -1 : 1));
            }
          }}
          className="shrink-0 cursor-grab rounded p-1 text-gray-400 focus-visible:ring-2 focus-visible:ring-indigo-500">
          <GripVertical className="h-4 w-4" />
        </button>
        {item.kind === 'TEXT' && <div className="relative min-w-0 flex-1">
          <span aria-hidden="true" style={textBlockCss(item.style, defaultStyle)}
            className="invisible block min-w-32 whitespace-pre-wrap rounded border px-2 py-1.5 text-base leading-6 [overflow-wrap:anywhere]">
            {(item.text || l.placeholder) + ' '}
          </span>
          <textarea style={{ ...textBlockCss(item.style, defaultStyle), backgroundColor,
              ...(backgroundColor === 'transparent' ? {
                backgroundColor: '#737373',
                backgroundImage: 'conic-gradient(#666666 25%, #808080 0 50%, #666666 0 75%, #808080 0)',
                backgroundSize: '16px 16px',
              } : {}),
            }} rows={1} name={index === 0 ? 'text' : `text-item-${index}`} aria-label={l.TEXT}
            className="absolute inset-0 h-full w-full resize-none overflow-hidden rounded border border-gray-300 bg-white px-2 py-1.5 text-base leading-6 shadow-sm [overflow-wrap:anywhere] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={item.text ?? ''} maxLength={500} placeholder={l.placeholder}
            onChange={event => update(index, { text: event.target.value })} />
        </div>}
        {item.kind === 'IMAGE' && <RichMessageImage inputRef={index === items.length - 1 ? imageInput : undefined}
          onUploadingChange={value => { setUploading(value); onUploadingChange(value); }}
          value={item.imageUrl ?? ''} onChange={imageUrl => update(index, { imageUrl })} />}
        {(item.kind === 'EMOJI' || item.kind === 'STICKER') && <button type="button"
          aria-label={`${item.kind === 'EMOJI' ? l.replaceEmoji : l.replaceSticker} ${index + 1}`}
          onClick={() => { setEditingIndex(index); setPicker(item.kind as 'EMOJI' | 'STICKER'); }}
          className="flex h-10 cursor-pointer items-center rounded px-2 text-2xl hover:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-500">
          <RichMessagePreview items={[item]} />
        </button>}
        {item.kind === 'TEXT' && <TextBlockStyles index={index} value={item.style} defaults={defaultStyle}
          onChange={style => update(index, { style })} />}
        <button type="button" aria-label={`${l.remove} ${index + 1}`}
          onClick={() => onChange(items.filter((_, i) => i !== index))}
          className="shrink-0 cursor-pointer rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 focus-visible:ring-2 focus-visible:ring-indigo-500">
          <X className="h-4 w-4" />
        </button>
      </div>)}
      <details className="relative" onKeyDown={event => { if (event.key === 'Escape') event.currentTarget.open = false; }}>
        <summary className={`flex h-10 cursor-pointer list-none items-center gap-2 rounded border border-gray-300 px-3 text-sm ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
          {l.addBlock}<ChevronDown className="h-4 w-4" />
        </summary>
        <div className="absolute left-0 top-full z-30 mt-1 w-72 max-w-[80vw] rounded border border-gray-200 bg-white p-1 shadow-lg">
          {(['TEXT', 'IMAGE', 'EMOJI', 'STICKER'] as const).map(kind => <button type="button" key={kind}
            disabled={items.length >= 24 || (kind === 'IMAGE' && items.filter(item => item.kind === 'IMAGE').length >= 8)}
            className="block w-full cursor-pointer rounded px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-40"
            onClick={event => {
              event.currentTarget.closest('details')?.removeAttribute('open');
              if (kind === 'TEXT') add({ kind: 'TEXT', text: '' });
              else if (kind === 'IMAGE') {
                flushSync(() => add({ kind: 'IMAGE', imageUrl: '', alt: '[image-not-available]' }));
                imageInput.current?.click();
              }
              else { setEditingIndex(null); setPicker(kind); }
            }}>
            <span className="block text-sm font-medium">{l[kind]}</span>
            <span className="mt-0.5 block text-xs text-gray-500">{l[kind === 'TEXT' ? 'textHelp' : kind === 'IMAGE' ? 'gifHelp' : kind === 'EMOJI' ? 'emojiHelp' : 'stickerHelp']}</span>
          </button>)}
        </div>
      </details>
    </div>
    <Modal isOpen={picker !== null} onClose={() => { if (!uploading) { setPicker(null); setEditingIndex(null); } }} closeDisabled={uploading} closeOnEsc={!uploading} closeOnBackdrop={!uploading} title={picker === 'EMOJI' ? l.EMOJI : l.STICKER} size="sm">
      {picker === 'EMOJI' ? <Suspense fallback={<div className="h-80 animate-pulse rounded bg-gray-50" />}>
        <EmojiPicker onSelect={text => choose({ kind: 'EMOJI', text })} />
      </Suspense> : picker === 'STICKER' && <StickerLibraryPicker
        onSelect={choose}
        canSelectImage={items.filter(item => item.kind === 'IMAGE').length < 8 || (editingIndex !== null && items[editingIndex]?.kind === 'IMAGE')}
        onBusy={value => { setUploading(value); onUploadingChange(value); }}
      />}
    </Modal>
    <p className="text-xs text-gray-500">{messageFallback(items).length}/500 · {items.length}/24</p>
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
  </fieldset>;
}
